/**
 * Агуулах — `stores.type = WAREHOUSE` нэгж бүрийн ДОТООД зураг.
 *
 * ⚠️ «Бүх гутал» (`/items`) хуудаснаас ЯЛГААТАЙ: тэр нь
 *    системд байгаа бүх гутлын шүүгддэг жагсаалт. Энэ нь НЭГ агуулах —
 *    түүний байршил, ажилтан, нөөц. Нэг таб = нэг агуулах.
 *
 * ⚠️ Агуулах бол тусдаа хүснэгт БИШ — `stores`-ийн нэг төрөл. Тиймээс
 *    «Агуулах нэмэх» товч нь `components/store-form.tsx`-ийг
 *    `fixedType="WAREHOUSE"`-ээр дуудна, шинэ маягт зохиохгүй.
 *
 * Гутлын жагсаалт нь `GET /items?storeId=` — сервер тал дээр шүүж,
 * cursor-оор хуудаслана. Тайлангийн хариунд гутал ОРОХГҮЙ.
 */

import { formatDate, formatMnt, formatQty } from '@boots/domain';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../components/data-table';
import { ItemTable } from '../components/item-table';
import { Meter } from '../components/meter';
import { Section } from '../components/section';
import { StatCard, StatGrid, StatPanel } from '../components/stat-card';
import { StoreForm } from '../components/store-form';
import { Button, Chip, Empty, ErrorNote, Pager, Spinner, Tabs } from '../components/ui';
import { api, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
    COST_ROLES, LOCATION_TYPE_LABEL, ROLE_LABEL, STATUS_LABEL,
    type ItemListRow, type ItemStatus, type Warehouse,
    type WarehouseLocationRow, type WarehouseReport, type WarehouseStaffRow,
} from '../lib/types';

const PER_PAGE = 12;

export function WarehousePage() {
  const { can, readOnly } = useAuth();
  const showCost = can(...COST_ROLES);
  // Салбар, агуулахын мөр ҮҮСГЭХ нь ADMIN — `ref.ts` `adminOnly`-той ижил
  const canAdd = can('ADMIN') && !readOnly;

  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api<Envelope<WarehouseReport>>('/reports/warehouses'),
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  const list = data.data.warehouses;
  const active = list.find((w) => w.id === params.get('w')) ?? list[0] ?? null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12.5px] text-ink-2">
          {formatQty(list.length)} агуулах · нөөц, байршил, ажилтан
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/setup/stores"
            className="rounded-full border border-hair px-4 py-2 text-[12.5px]
              whitespace-nowrap transition hover:bg-well"
          >
            Байршил удирдах →
          </Link>
          {canAdd ? <Button onClick={() => setAdding(true)}>＋ Агуулах нэмэх</Button> : null}
        </div>
      </div>

      {active === null ? (
        <Section title="Агуулах">
          <Empty>
            {canAdd
              ? 'Агуулах бүртгэгдээгүй — «Агуулах нэмэх» товчоор эхлүүлнэ'
              : 'Агуулах бүртгэгдээгүй'}
          </Empty>
        </Section>
      ) : (
        <>
          <Tabs
            value={active.id}
            onChange={(id) => setParams({ w: id }, { replace: true })}
            tabs={list.map((w) => ({ key: w.id, label: w.name, count: w.items }))}
          />
          <WarehouseDetail key={active.id} w={active} showCost={showCost} />
        </>
      )}

      {adding ? (
        <StoreForm
          store={null}
          fixedType="WAREHOUSE"
          onClose={() => setAdding(false)}
          onDone={() => setAdding(false)}
        />
      ) : null}
    </div>
  );
}

/** Нэг агуулахын бүх зүйл. `key`-ээр дахин холбогддог тул төлөв цэвэрлэгдэнэ. */
function WarehouseDetail({ w, showCost }: { w: Warehouse; showCost: boolean }) {
  const pct = w.items ? Math.round((w.sellable / w.items) * 100) : 0;

  return (
    <div className="flex flex-col gap-3">
      <StatPanel label={`${w.name} — статистик`}>
        <div className="flex flex-col gap-3">
          <StatGrid>
            <StatCard label="Нийт нөөц" icon="box" value={`${formatQty(w.items)} ш`} />
            <StatCard
              label="Зарагдахад бэлэн"
              icon="store"
              value={`${formatQty(w.sellable)} ш`}
              meter={pct}
              meterTone={w.sellable === 0 && w.items > 0 ? 'alert' : 'accent'}
            />
            <StatCard
              label="Шошгогүй"
              icon="box"
              value={`${formatQty(w.unlabeled)} ш`}
              delta={
                w.unlabeled > 0
                  ? { text: 'Серийн дугаар хүлээж буй', good: false }
                  : { text: 'Бүгд шошготой', good: true }
              }
            />
            <StatCard
              label="Нөөцийн өртөг"
              icon="coins"
              value={w.landedCostMnt === null ? '•••' : formatMnt(w.landedCostMnt)}
              pending={w.landedCostMnt === null}
              delta={{ text: 'Тээвэр, гааль оруулсан' }}
            />
          </StatGrid>

          {/* Төлвийн задаргаа — чип дарвал тэр төлвөөр шүүсэн жагсаалт */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-2">Төлвөөр:</span>
            {(Object.keys(STATUS_LABEL) as ItemStatus[])
              .filter((s) => (w.byStatus[s] ?? 0) > 0)
              .map((s) => (
                <Link
                  key={s}
                  to={`/items?storeId=${w.id}&status=${s}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-hair
                    bg-panel px-[11px] py-1 text-xs text-ink-2 transition hover:bg-accent-100/50"
                >
                  {STATUS_LABEL[s]}
                  <span className="num font-medium text-ink">{formatQty(w.byStatus[s] ?? 0)}</span>
                </Link>
              ))}
          </div>

          <div className="flex flex-col gap-[5px] border-t border-hair pt-2.5">
            <div className="flex gap-2.5 text-xs text-ink-2">
              <span>Зарагдахад бэлэн</span>
              <span className="num ml-auto">{pct}%</span>
            </div>
            <Meter value={pct} label={`Зарагдахад бэлэн ${pct}%`} />
          </div>
        </div>
      </StatPanel>

      <Section
        title="Байршил"
        subtitle={`${formatQty(w.locations.length)} байршил`}
        note={w.address ?? undefined}
      >
        <DataTable
          rows={w.locations}
          columns={locationColumns()}
          rowKey={(l) => l.id}
          empty={<Empty>Байршил бүртгэгдээгүй</Empty>}
        />
      </Section>

      <Section title="Хариуцсан ажилтан" subtitle={`${formatQty(w.staff.length)} хүн`}>
        <DataTable
          rows={w.staff}
          columns={staffColumns()}
          rowKey={(u) => u.id}
          rowClass={(u) => (u.isActive ? '' : 'opacity-55')}
          empty={<Empty>Энэ агуулахад ажилтан хуваарилагдаагүй</Empty>}
        />
      </Section>

      <WarehouseItems w={w} showCost={showCost} />
    </div>
  );
}

/** Агуулах дахь бүх гутал — `GET /items?storeId=`. */
function WarehouseItems({ w, showCost }: { w: Warehouse; showCost: boolean }) {
  const [status, setStatus] = useState<ItemStatus | ''>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [stack, setStack] = useState<string[]>([]);

  useEffect(() => {
    setCursor(null);
    setStack([]);
  }, [status]);

  const query = new URLSearchParams({ limit: String(PER_PAGE), storeId: w.id });
  if (status) query.set('status', status);
  if (cursor) query.set('cursor', cursor);

  const { data, isPending, isFetching, error } = useQuery({
    queryKey: ['items', query.toString()],
    queryFn: () => api<PagedEnvelope<ItemListRow>>(`/items?${query.toString()}`),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data ?? [];

  return (
    <Section
      title="Гутал"
      subtitle={data ? `${formatQty(data.meta.total)} ширхэг` : '…'}
      actions={
        <Link
          to={`/items?storeId=${w.id}`}
          className="text-[12.5px] whitespace-nowrap text-accent-700 hover:underline"
        >
          Бүх шүүлтүүрээр харах →
        </Link>
      }
    >
      <div className="flex flex-wrap gap-1.5">
        <Chip active={!status} onClick={() => setStatus('')}>Бүгд</Chip>
        {(Object.keys(STATUS_LABEL) as ItemStatus[])
          .filter((s) => (w.byStatus[s] ?? 0) > 0)
          .map((s) => (
            <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
              {STATUS_LABEL[s]}
            </Chip>
          ))}
      </div>

      {error ? <ErrorNote>{(error as Error).message}</ErrorNote> : null}

      {isPending ? (
        <Spinner />
      ) : (
        <ItemTable rows={rows} showCost={showCost} empty={<Empty>Гутал алга</Empty>} />
      )}

      <Pager
        page={stack.length + 1}
        canPrev={stack.length > 0 && !isFetching}
        canNext={Boolean(data?.meta.nextCursor) && !isFetching}
        onPrev={() => {
          const prev = [...stack];
          prev.pop();
          setStack(prev);
          setCursor(prev.at(-1) ?? null);
        }}
        onNext={() => {
          const next = data?.meta.nextCursor;
          if (!next) return;
          setStack((s) => [...s, next]);
          setCursor(next);
        }}
      />
    </Section>
  );
}

function locationColumns(): Column<WarehouseLocationRow>[] {
  return [
    { key: 'code', header: 'Код', cell: (l) => l.code, nowrap: true, mobile: 'title' },
    { key: 'name', header: 'Нэр', cell: (l) => <span className="text-pretty">{l.name}</span> },
    { key: 'type', header: 'Төрөл', cell: (l) => <span className="text-ink-2">{LOCATION_TYPE_LABEL[l.type] ?? l.type}</span> },
    {
      key: 'items',
      header: 'Нөөц',
      align: 'right',
      mobile: 'value',
      cell: (l) => `${formatQty(l.items)} ш`,
    },
    { key: 'sellable', header: 'Зарагдана', cell: (l) => (l.isSellable ? '✓' : '—') },
    { key: 'countable', header: 'Тоологдоно', cell: (l) => (l.isCountable ? '✓' : '—') },
  ];
}

function staffColumns(): Column<WarehouseStaffRow>[] {
  return [
    {
      key: 'name',
      header: 'Нэр',
      mobile: 'title',
      cell: (u) => (
        <span className="flex flex-col">
          <span className="font-medium text-pretty">{u.fullName}</span>
          <span className="num text-xs text-ink-2">{u.username}</span>
        </span>
      ),
    },
    { key: 'role', header: 'Дүр', mobile: 'value', cell: (u) => ROLE_LABEL[u.role] },
    { key: 'phone', header: 'Утас', nowrap: true, cell: (u) => <span className="num">{u.phone ?? '—'}</span> },
    {
      key: 'state',
      header: 'Төлөв',
      cell: (u) => (u.isActive ? 'Идэвхтэй' : 'Идэвхгүй'),
    },
    {
      key: 'login',
      header: 'Сүүлд нэвтэрсэн',
      align: 'right',
      cell: (u) => (
        <span className="text-ink-2">{u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}</span>
      ),
    },
  ];
}
